import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import API_BASE_URL from '../config/api';
const T = { bg: '#FAF9F6', dark: '#1A2238', ink: '#0F1420', gold: '#C39B62', muted: '#9a9a9a', border: '#E6E8EA' };

const inputCls = "w-full px-5 py-4 rounded-lg text-base focus:outline-none transition-colors";
const inputStyle = { backgroundColor: T.bg, border: `1px solid ${T.border}`, color: T.ink };
const onFocus = e => e.target.style.borderColor = T.gold;
const onBlur = e => e.target.style.borderColor = T.border;

const StatusScreen = ({ children }) => (
  <div className="h-screen flex items-center justify-center" style={{ backgroundColor: T.bg }}>
    <div className="rounded-2xl p-12 text-center max-w-sm mx-4" style={{ border: `1px solid ${T.border}` }}>{children}</div>
  </div>
);

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const emailFromUrl = searchParams.get('email');

  const [tokenStatus, setTokenStatus] = useState('loading');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token || !emailFromUrl) { setTokenStatus('invalid'); return; }
      try {
        const res = await fetch(`${API_BASE_URL}/vrv-auth-verify-token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ emailID: emailFromUrl, token }),
        });
        const data = await res.json();
        if (res.ok && data.valid) setTokenStatus('valid');
        else setTokenStatus(data.message?.toLowerCase().includes('expired') ? 'expired' : 'invalid');
      } catch { setTokenStatus('invalid'); }
    };
    verifyToken();
  }, [token, emailFromUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    if (newPassword !== confirmPassword) { setMessage('Passwords do not match'); setMessageType('error'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/vrv-auth-reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emailID: emailFromUrl, newPassword, confirmPassword }),
      });
      const data = await res.json();
      if (res.ok) { setSuccess(true); setTimeout(() => navigate('/login'), 3000); }
      else { setMessage(data.message || data.error || 'Password reset failed'); setMessageType('error'); }
    } catch { setMessage('Error connecting to server'); setMessageType('error'); }
    setLoading(false);
  };

  if (tokenStatus === 'loading') return (
    <StatusScreen>
      <p className="text-sm animate-pulse" style={{ color: T.muted }}>Verifying reset link...</p>
    </StatusScreen>
  );

  if (tokenStatus === 'invalid') return (
    <StatusScreen>
      <p className="text-xs font-bold tracking-[0.35em] uppercase mb-3" style={{ color: '#b91c1c' }}>Invalid Link</p>
      <h2 className="text-2xl font-black mb-3" style={{ color: T.ink }}>Link Not Valid</h2>
      <p className="text-sm mb-6" style={{ color: T.muted }}>This reset link is invalid or has already been used.</p>
      <Link to="/forgot-password" className="font-black text-xs tracking-widest uppercase" style={{ color: T.dark }}>Request New Link →</Link>
    </StatusScreen>
  );

  if (tokenStatus === 'expired') return (
    <StatusScreen>
      <p className="text-xs font-bold tracking-[0.35em] uppercase mb-3" style={{ color: T.gold }}>Expired</p>
      <h2 className="text-2xl font-black mb-3" style={{ color: T.ink }}>Link Expired</h2>
      <p className="text-sm mb-6" style={{ color: T.muted }}>This password reset link has expired. Please request a new one.</p>
      <Link to="/forgot-password" className="font-black text-xs tracking-widest uppercase" style={{ color: T.dark }}>Request New Link →</Link>
    </StatusScreen>
  );

  return (
    <div className="h-screen flex overflow-hidden" style={{ backgroundColor: T.bg }}>
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-16" style={{ backgroundColor: T.dark, borderRight: `1px solid ${T.ink}` }}>
        <img src="/images/VRV.png" alt="VRV" className="h-24 w-24 object-contain" style={{ filter: 'drop-shadow(0 0 30px rgba(195,155,98,0.6)) drop-shadow(0 0 60px rgba(195,155,98,0.25))' }} />
        <div>
          <p className="text-xs font-bold tracking-[0.35em] uppercase mb-6" style={{ color: T.gold }}>Secure Access</p>
          <h2 className="text-6xl md:text-8xl font-black leading-none mb-8" style={{ color: '#FAF9F6' }}>
            CREATE<br /><span style={{ color: T.gold }}>NEW</span><br />PASSWORD
          </h2>
          <div className="w-16 h-0.5 mb-8" style={{ backgroundColor: T.gold }}></div>
          <p className="text-sm leading-relaxed max-w-sm" style={{ color: 'rgba(250,249,246,0.5)' }}>
            Choose a strong password to keep your VRV account secure.
          </p>
        </div>
        <p className="text-xs italic" style={{ color: 'rgba(195,155,98,0.35)' }}>"In service of Lord Vishnu and the Vaikhanasa tradition"</p>
      </div>

      {/* Right Panel */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-16 py-12" style={{ backgroundColor: T.bg }}>
        <div className="w-full">
          {!success ? (
            <>
              <p className="text-xs font-bold tracking-[0.25em] uppercase mb-3" style={{ color: T.gold }}>Almost There</p>
              <h1 className="text-5xl font-black mb-2" style={{ color: T.ink }}>Reset Password</h1>
              <p className="text-base mb-12" style={{ color: T.muted }}>Set a new password for <span className="font-bold" style={{ color: T.ink }}>{emailFromUrl}</span></p>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-xs font-bold tracking-widest uppercase mb-2" style={{ color: T.ink }}>New Password</label>
                  <input type="password" placeholder="••••••••" className={inputCls} style={inputStyle}
                    onFocus={onFocus} onBlur={onBlur} value={newPassword} onChange={e => setNewPassword(e.target.value)} required />
                </div>
                <div>
                  <label className="block text-xs font-bold tracking-widest uppercase mb-2" style={{ color: T.ink }}>Confirm New Password</label>
                  <input type="password" placeholder="••••••••" className={inputCls} style={inputStyle}
                    onFocus={onFocus} onBlur={onBlur} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required />
                </div>
                <button type="submit" disabled={loading || !newPassword || !confirmPassword}
                  className="w-full font-black py-5 rounded-lg text-sm tracking-widest uppercase transition-opacity disabled:opacity-50"
                  style={{ backgroundColor: T.dark, color: '#FAF9F6' }}>
                  {loading ? 'Resetting...' : 'Reset Password →'}
                </button>
              </form>
              {message && (
                <div className="mt-4 text-sm px-4 py-3 rounded-lg" style={{ color: messageType === 'success' ? '#15803d' : '#b91c1c', backgroundColor: messageType === 'success' ? '#f0fdf4' : '#fef2f2', border: `1px solid ${messageType === 'success' ? '#bbf7d0' : '#fecaca'}` }}>
                  {message}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6" style={{ backgroundColor: T.gold }}>
                <svg className="w-8 h-8" style={{ color: T.dark }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-xs font-bold tracking-[0.25em] uppercase mb-3" style={{ color: T.gold }}>Success</p>
              <h1 className="text-5xl font-black mb-2" style={{ color: T.ink }}>Password Reset!</h1>
              <p className="text-base mb-4" style={{ color: T.muted }}>Your password has been reset successfully.</p>
              <p className="text-sm" style={{ color: T.muted }}>Redirecting to login...</p>
            </>
          )}
          <p className="text-base mt-10 text-center" style={{ color: T.muted }}>
            <Link to="/login" className="font-bold" style={{ color: T.dark }}>← Back to Login</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
